<template>
  <v-card
    elevation="0"
    class="v-card"
  >
    <v-card-title
      class="v-card-title"
    >
      <v-row>
        <v-col
          class="d-flex justify-start align-end"
        >
          {{ $t('road-maintenance/aggregator.attachments') }}
        </v-col>

        <v-col
          class="d-flex justify-end"
        >
          <universal-button
            v-if="canEdit || createMode"
            :label="$t('road-maintenance/aggregator.add_new_attachment')"
            text
            color="#163D73"
            prepend-icon="mdi-plus"
            @click="onNewAttachmentClick"
          />
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text
      class="v-card-text"
    >
      <v-row
        v-for="(chunkedRow, chunkedRowIndex) in chunkFields()"
        :key="chunkedRowIndex"
        class="main-row"
      >
        <v-col
          v-for="(attachment, attachmentIndex) in chunkedRow"
          :key="attachmentIndex"
          class="attachment-col"
          :cols="getFieldCols(attachment)"
        >
          <div>
            <span
              class="field-label"
            >
              {{ config.baseLabel ? config.baseLabel : '' }}
            </span>
<!--            Attachment type field-->
            <autocomplete-field
              v-if="attachment.type_id"
              v-model="formDataCopy[config.attachmentsKey][attachment.index].type_id"
              :items="{ [attachment.type_id]: { data: attachment.items } || { data: [] } }"
              :validation-errors="validationErrors"
              :options="{
                allowSearch: false,
                fetchInitial: false,
                custom_display_value: (value) => {
                  return value && value.description ? $t(value.description) : null
                }
              }"
              :custom-field-key="makeArrayItemCustomKey(attachmentIndex)"
              :field="{
                key: attachment.type_id
              }"
              :disabled="!canEdit && !createMode"
              @clear-validation-errors="$emit('clear-validation-errors', makeArrayItemCustomKey(attachmentIndex))"
            />

<!--            File preview-->
            <div
              class="mt-2"
              style="height: 160px; background-color: #EDF9FF; border: 1px solid #B9E7FF; border-radius: 8px; position: relative; display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center;"
            >
<!--              Delete file icon in the top-right corner-->
              <v-img
                v-if="canEdit || createMode"
                src="/img/icons/delete-file-icon.svg"
                id="delete-file-image"
                @click="onFileDelete(attachmentIndex, attachment)"
              />
              <div
                style="width: 100%;"
              >
<!--                File icon/image-->
                <v-row
                  class="file-row"
                >
                  <v-col
                    class="file-icon-col"
                    @click="download(attachment)"
                  >
                    <template
                      v-if="attachment.icon && attachment.icon.src"
                    >
                      <v-icon
                        id="file-icon"
                        :class="!attachment || !attachment.file_id ? 'default-cursor' : ''"
                        :style="attachment.icon.style && isObject(attachment.icon.style) ? attachment.icon.style : {}"
                      >
                        {{ attachment.icon.src }}
                      </v-icon>
                    </template>
                    <template
                      v-else-if="attachment.image && attachment.image.src"
                    >
                      <v-img
                        :src="attachment.image.src"
                        id="file-image"
                        :class="!attachment || !attachment.file_id ? 'default-cursor' : ''"
                        :style="attachment.image.style && isObject(attachment.image.style) ? attachment.image.style : {}"
                      />
                    </template>
                  </v-col>
                </v-row>

<!--                File name-->
                <v-row
                  class="file-row"
                >
                  <v-col
                    id="file-name-col"
                    :class="!attachment || !attachment.file_id ? 'default-cursor' : ''"
                    @click="download(attachment)"
                  >
                    {{ attachment && attachment.file_name ? attachment.file_name : '-' }}
                  </v-col>
                </v-row>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row
        style="margin: 0;"
      >
        <v-col
          style="font-family: 'Satoshi', sans-serif; font-size: 14px; font-weight: 400; line-height: 20px;"
        >
          {{ $t('road-maintenance/aggregator.attachments_note') }}
        </v-col>
      </v-row>
    </v-card-text>
    <input
      type="file"
      ref="attachment_input"
      class="d-none"
      :accept="config.accept && typeof config.accept === 'string' ? config.accept: '*'"
      :multiple="true"
      enctype="multipart/form-data"
      @change="event => onFilesInput(event)"
    />
  </v-card>
</template>

<script>
import AutocompleteField from '@/global/components/view-layouts/tabs-layout/components/fields/AutocompleteField.vue'
import { cloneDeep, isObject } from 'lodash'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'

const MAX_COLUMNS = 12
const DEFAULT_COLUMNS_COUNT = 2
export default {
  name: 'Attachments',

  components: {
    UniversalButton,
    AutocompleteField
  },

  props: {
    formData: {
      type: Object,
      default: () => ({})
    },
    config: {
      type: Object,
      default: () => ({})
    },
    edit: {
      type: Boolean,
      default: false
    },
    createMode: {
      type: Boolean,
      default: false
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    },
    updateData: {
      type: Function,
      default: () => {}
    }
  },

  data () {
    return {
      formDataCopy: {}
    }
  },

  computed: {
    attachments () {
      if (this.config?.attachmentsKey && this.formDataCopy[this.config.attachmentsKey]?.length) {
        return this.formDataCopy[this.config.attachmentsKey].map((item, index) => {
          return {
            ...item,
            ...{
              items: this.config?.dropdownValues?.attachment_type ?? [],
              index
            }
          }
        })
      }

      return []
    },

    canEdit () {
      return this.edit
    }
  },

  watch: {
    formData: {
      immediate: true,
      deep: true,
      handler (val) {
        if (val && isObject(val)) {
          this.formDataCopy = val
        }
      }
    }
  },

  methods: {
    isObject,

    onFileDelete (fileIndex, attachment) {
      this.formDataCopy[this.config.attachmentsKey].splice(fileIndex, 1)
      const { onFileDelete } = this.config || {}

      if (typeof onFileDelete === 'function') {
        onFileDelete({
          file: attachment,
          updateData: (val) => this.updateData(val),
          formData: cloneDeep(this.formData)
        })
      }
    },

    makeArrayItemCustomKey (index) {
      return (this.config?.attachmentsKey ?? '') + '.' + index + '.' + 'type'
    },

    onNewAttachmentClick () {
      this.$refs.attachment_input.click()
    },

    onFilesInput (event) {
      const { onFilesInput } = this.config || {}

      if (typeof onFilesInput === 'function') {
        onFilesInput({
          filesEvent: event,
          updateData: this.updateData,
          formData: this.formDataCopy
        })
      }
    },

    async download (attachment) {
      let objectURL = null
      try {
        const { file_id: fileId } = attachment || {}
        if (fileId) {
          const url = new URL(`${window.location.origin}/api/road-maintenance/traffic-regulation/${this.$route.params.id}/attachment/${fileId}`)

          const response = await fetch(url.toString(), {
            method: 'POST'
          })

          if (!response.ok) {
            console.error(`Failed to show PDF: HTTP ${response.status} - ${response.statusText}`)
            return
          }

          const contentType = response.headers.get('Content-type')
          const contentDisposition = response.headers.get('Content-Disposition')
          const arrayBuffer = await response.arrayBuffer()

          // Create a Blob from the response data
          const blob = new Blob([new Uint8Array(arrayBuffer)], {
            type: contentType
          })

          // Generate object URL for downloading
          objectURL = URL.createObjectURL(blob)
          const filename = contentDisposition?.split('filename=')[1] ?? 'File'

          // Trigger download
          const link = document.createElement('a')
          link.href = objectURL
          link.download = filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      }
      catch (exception) {
        console.error(exception.message || exception)
      }
      finally {
        if (objectURL) {
          URL.revokeObjectURL(objectURL)
        }
      }
    },

    chunkFields () {
      const grouped = {}
      const columnCount = this.config?.columnsCount ?? DEFAULT_COLUMNS_COUNT

      if (this.attachments && this.attachments.length) {
        this.attachments.forEach((field, index) => {
          if (!grouped[field.row]) grouped[field.row] = []
          grouped[field.row].push({
            ...field,
            index
          })
        })

        // Ensure each row fills up to columnCount by adding placeholders if needed
        Object.keys(grouped).forEach(rowIndex => {
          grouped[rowIndex] = this.fillRowWithPlaceholders(grouped[rowIndex], columnCount)
        })

        return Object.values(grouped)
      }

      return []
    },

    getFieldCols (field) {
      const columnCount = this.config?.columnsCount ?? DEFAULT_COLUMNS_COUNT
      const colWidth = Math.floor(MAX_COLUMNS / columnCount)
      const fieldColSpan = field.colSpan || 1

      return this.$vuetify.breakpoint.mobile ? 12 : fieldColSpan * colWidth
    },

    fillRowWithPlaceholders (rowFields, columnCount) {
      const filledRow = []
      let currentColumn = 1

      rowFields.forEach((field) => {
        // Calculate offset for any gap before the field's column position
        if (field.column > currentColumn) {
          const gapSize = field.column - currentColumn
          filledRow.push({
            isPlaceholder: true,
            colSpan: gapSize
          })
          currentColumn += gapSize
        }

        // Add the actual field with its specified colSpan
        filledRow.push(field)
        currentColumn += field.colSpan || 1

        // Check if we've reached or exceeded the column count for the row
        if (currentColumn >= columnCount) {
          currentColumn = columnCount // Keep it capped at columnsCount
        }
      })

      // Fill remaining space with placeholders if row doesn't reach `columnsCount`
      while (currentColumn < columnCount) {
        filledRow.push({
          isPlaceholder: true,
          colSpan: 1
        })
        currentColumn += 1
      }

      return filledRow
    }
  }
}
</script>

<style scoped>
.field-label {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #09090B;
}

.file-row {
  margin: 0;
  justify-content: center;
  justify-items: center;
}

.file-icon-col {
  display: flex;
  justify-content: center;
  justify-items: center;
  padding: 0;
}

#file-icon {
  cursor: pointer;
}

#file-image {
  cursor: pointer;
}

#file-name-col {
  cursor: pointer;
  display: flex;
  flex-direction:column;
  justify-content: center;
  justify-items: center;
  padding: 0;
  font-size: 16px;
  line-height: 24px;
  color: #09090B;
  max-width: fit-content;
}

#delete-file-image {
  position: absolute;
  width: 32px;
  height: 32px;
  top: 3px;
  right: 8px;
  cursor: pointer;
}

.v-card-text {
  border: 1px solid #E5E7EB;
  border-radius: 16px !important;
  padding: 16px;
}

.v-card {
  margin: 0 16px 16px 16px;
}

.v-card-title {
  padding-left: 0;
  padding-top: 0;
  font-family: 'Satoshi', sans-serif;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
}

.default-cursor {
  cursor: default !important;
}

@media (max-width: 801px) {
  .main-row {
    flex-direction: column;
  }

  .attachment-col {
    width: 100% !important;
    max-width: unset;
  }
}
</style>
